import { useState } from "react";
import { Card } from "@/components/ui/card";
import { CalendarContentItem } from "@/hooks/useContentCalendar";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Linkedin, FileText, Calendar as CalendarIcon, Clock, Filter, Plus, PenLine, List, Grid3X3, LayoutGrid } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalendarMonthView } from "@/components/blog-management/calendar/CalendarMonthView";
import { CalendarWeekView } from "@/components/blog-management/calendar/CalendarWeekView";
import { CalendarDayView } from "@/components/blog-management/calendar/CalendarDayView";

// Item component for displaying individual content pieces in list view
const ContentCalendarItem = ({ item, onClick }: { 
  item: CalendarContentItem;
  onClick: (item: CalendarContentItem) => void;
}) => {
  const statusColors: Record<string, string> = {
    draft: "bg-gray-200 text-gray-800",
    scheduled: "bg-blue-200 text-blue-800",
    published: "bg-green-200 text-green-800",
    failed: "bg-red-200 text-red-800",
  };
  
  const statusColor = statusColors[item.status.toLowerCase()] || "bg-gray-200 text-gray-800";
  
  return (
    <Card
      className="p-3 mb-2 cursor-pointer hover:shadow-md transition-shadow"
      onClick={() => onClick(item)}
    >
      <div className="flex items-start gap-2">
        {item.sourceType === "blog" ? (
          <FileText className="text-blue-500 h-5 w-5 mt-0.5 flex-shrink-0" />
        ) : (
          <Linkedin className="text-blue-700 h-5 w-5 mt-0.5 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-sm truncate">{item.title}</h3>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <Badge className={statusColor}>
              {item.status}
            </Badge>
            {item.scheduledFor && (
              <div className="flex items-center text-xs text-gray-600">
                <CalendarIcon className="h-3 w-3 mr-1" />
                {format(new Date(item.scheduledFor), "MMM d, yyyy")}
              </div>
            )}
            {!item.scheduledFor && item.publishedAt && (
              <div className="flex items-center text-xs text-gray-600">
                <Clock className="h-3 w-3 mr-1" />
                {format(new Date(item.publishedAt), "MMM d, yyyy")}
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

// Item details dialog component
const ContentItemDetailsDialog = ({ 
  item, 
  open, 
  onOpenChange,
  onEdit
}: {
  item: CalendarContentItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: (item: CalendarContentItem) => void;
}) => {
  if (!item) return null;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{item.title}</DialogTitle>
        </DialogHeader>
        
        <div className="grid gap-4 py-3">
          {/* Display item content preview */}
          {item.content && (
            <div className="max-h-28 overflow-y-auto text-sm text-gray-600 border-l-2 border-gray-200 pl-3">
              <div dangerouslySetInnerHTML={{ 
                __html: item.content.length > 200 
                  ? item.content.substring(0, 200) + "..." 
                  : item.content 
              }} />
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <div className="text-xs font-medium text-slate-500">Type</div>
              <div className="flex items-center">
                {item.sourceType === "blog" ? (
                  <>
                    <FileText className="h-3.5 w-3.5 mr-1 text-blue-600" />
                    <span>Blog Post</span>
                  </>
                ) : (
                  <>
                    <Linkedin className="h-3.5 w-3.5 mr-1 text-blue-700" />
                    <span>LinkedIn Post</span>
                  </>
                )}
              </div>
            </div>
            
            <div className="space-y-1">
              <div className="text-xs font-medium text-slate-500">Status</div>
              <Badge 
                variant="outline"
                className={
                  item.status === 'published' ? "bg-green-100 text-green-800" :
                  item.status === 'scheduled' ? "bg-blue-100 text-blue-800" :
                  "bg-amber-100 text-amber-800"
                }
              >
                {item.status}
              </Badge>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            {item.scheduledFor && (
              <div className="space-y-1">
                <div className="text-xs font-medium text-slate-500">Scheduled for</div>
                <div className="text-sm flex items-center">
                  <CalendarIcon className="h-3.5 w-3.5 mr-1 text-slate-500" />
                  {format(new Date(item.scheduledFor), "PPP")}
                </div>
              </div>
            )}
            
            {item.publishedAt && (
              <div className="space-y-1">
                <div className="text-xs font-medium text-slate-500">Published on</div>
                <div className="text-sm flex items-center">
                  <Clock className="h-3.5 w-3.5 mr-1 text-slate-500" />
                  {format(new Date(item.publishedAt), "PPP")}
                </div>
              </div>
            )}
          </div>
          
          {item.image && (
            <div className="space-y-2">
              <div className="text-xs font-medium text-slate-500">Featured Image</div>
              <img 
                src={item.image} 
                alt={item.title} 
                className="h-32 w-full object-cover rounded-md" 
                onError={(e) => {
                  e.currentTarget.src = "https://placehold.co/600x400?text=Image+Preview";
                }}
              />
            </div>
          )}
        </div>
        
        <DialogFooter className="flex justify-between sm:justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button size="sm" onClick={() => onEdit(item)}>
            <PenLine className="h-4 w-4 mr-1" />
            Edit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main content calendar view component
interface ContentCalendarViewProps {
  items: CalendarContentItem[];
  currentDate: Date;
  isLoading?: boolean;
  onFilter?: () => void;
  onNewContent?: () => void;
  viewMode: "list" | "month" | "week" | "day";
  onViewModeChange: (mode: "list" | "month" | "week" | "day") => void;
}

export const ContentCalendarView = ({ 
  items, 
  currentDate,
  isLoading = false,
  onFilter,
  onNewContent,
  viewMode = "list",
  onViewModeChange
}: ContentCalendarViewProps) => {
  const navigate = useNavigate();
  const [selectedItem, setSelectedItem] = useState<CalendarContentItem | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  
  const handleItemClick = (item: CalendarContentItem) => {
    setSelectedItem(item);
    setShowDetails(true);
  };
  
  const handleEdit = (item: CalendarContentItem) => {
    setShowDetails(false);
    
    if (item.sourceType === "blog") {
      navigate(`/dashboard/blog/posts/${item.id}`);
    } else if (item.sourceType === "linkedin") {
      navigate(`/dashboard/linkedin/posts/${item.id}`);
    }
  };
  
  if (isLoading) {
    return <div className="flex items-center justify-center p-8">
      <CalendarIcon className="h-6 w-6 animate-pulse text-primary/50 mr-2" />
      <span>Loading calendar data...</span>
    </div>;
  }
  
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-muted-foreground">
        <CalendarIcon className="h-12 w-12 mb-2" />
        <p className="mb-4">No content scheduled for this period</p>
        
        <div className="flex gap-2">
          {onFilter && (
            <Button variant="outline" onClick={onFilter}>
              <Filter className="h-4 w-4 mr-1" />
              Filter
            </Button>
          )}
          
          {onNewContent && (
            <Button onClick={onNewContent}>
              <Plus className="h-4 w-4 mr-1" />
              Create Content
            </Button>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      <div className="flex justify-end mb-4">
        <Tabs value={viewMode} onValueChange={(value) => onViewModeChange(value as "list" | "month" | "week" | "day")}>
          <TabsList>
            <TabsTrigger value="list" className="flex items-center gap-1">
              <List size={16} />
              <span className="hidden sm:inline">List</span>
            </TabsTrigger>
            <TabsTrigger value="month" className="flex items-center gap-1">
              <LayoutGrid size={16} />
              <span className="hidden sm:inline">Month</span>
            </TabsTrigger>
            <TabsTrigger value="week" className="flex items-center gap-1">
              <Grid3X3 size={16} />
              <span className="hidden sm:inline">Week</span>
            </TabsTrigger>
            <TabsTrigger value="day" className="flex items-center gap-1">
              <CalendarIcon size={16} />
              <span className="hidden sm:inline">Day</span>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      
      {viewMode === "list" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item) => (
            <ContentCalendarItem
              key={`${item.sourceType}-${item.id}`}
              item={item}
              onClick={handleItemClick}
            />
          ))}
        </div>
      )}
      
      {viewMode === "month" && (
        <CalendarMonthView 
          currentDate={currentDate}
          items={items}
          onItemClick={handleItemClick}
        />
      )}
      
      {viewMode === "week" && (
        <CalendarWeekView 
          currentDate={currentDate}
          items={items}
          onItemClick={handleItemClick}
        />
      )}
      
      {viewMode === "day" && (
        <CalendarDayView 
          currentDate={currentDate}
          items={items}
          onItemClick={handleItemClick}
        />
      )}
      
      <ContentItemDetailsDialog
        item={selectedItem}
        open={showDetails}
        onOpenChange={setShowDetails}
        onEdit={handleEdit}
      />
    </div>
  );
};
